import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import {
  deleteAccount,
  fetchAccounts,
  sendActivationMail,
  sendResetPassword,
  updateAccountStatus,
} from '@/api/accounts.api'
import type { Account } from '@/types/api'
import { buildColumns } from './columns'
import { AccountFilterPanel } from './AccountFilterPanel'
import type { AccountFilterValues } from './AccountFilterPanel'
import { AccountDetailDrawer } from './AccountDrawer'
import { AccountFormDrawer } from './AccountFormDrawer'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { usePermission } from '@/hooks/use-permission'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportUser, useTableExport } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: AccountFilterValues = {
  fullName: '',
  email: '',
  unitId: undefined,
  unitName: undefined,
  status: undefined,
  time: undefined,
}

export function AccountsPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const canCreate = usePermission('account-create')
  const canView = usePermission('account-view')
  const canUpdate = usePermission('account-update')
  const canDelete = usePermission('account-delete')
  const canResetPass = usePermission('account-reset-pass')
  const canExport = usePermission('account-export')

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<AccountFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<AccountFilterValues>(DEFAULT_FILTER)
  const [isExporting, setIsExporting] = useState(false)
  const [viewingAccount, setViewingAccount] = useState<Account | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null)
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null)
  const [togglingAccount, setTogglingAccount] = useState<Account | null>(null)

  const queryClient = useQueryClient()

  const filterParams = useMemo(() => {
    const fromDate = activeFilters.time?.from
      ? formatStartDate(activeFilters.time.from)
      : undefined

    const toDateRaw = activeFilters.time?.to || activeFilters.time?.from

    const toDate = toDateRaw ? formatEndDate(toDateRaw) : undefined

    return {
      page: pageIndex,
      limit: pageSize,
      fullName: activeFilters.fullName || undefined,
      email: activeFilters.email || undefined,
      unitId: showUnitColumn ? activeFilters.unitId : currentUser?.unitId,
      status: activeFilters.status,
      fromDate,
      toDate,
    }
  }, [pageIndex, pageSize, activeFilters, showUnitColumn, currentUser?.unitId])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accounts', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) =>
      fetchAccounts(
        filterParams.page,
        filterParams.limit,
        {
          fullName: filterParams.fullName,
          email: filterParams.email,
          unitId: filterParams.unitId,
          status: filterParams.status,
          fromDate: filterParams.fromDate,
          toDate: filterParams.toDate,
        },
        signal,
      ),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })
  const hasData = Boolean(data?.items && data.items.length > 0)

  const { mutate: toggleAccountStatus, isPending: isToggling } = useMutation({
    mutationFn: (account: Account) =>
      updateAccountStatus(account, account.status === 1 ? 0 : 1),
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Cập nhật trạng thái tài khoản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onSettled: () => {
      setTogglingAccount(null)
    },
  })

  const { mutate: removeAccount, isPending: isRemoving } = useMutation({
    mutationFn: (account: Account) => deleteAccount(account.id),
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Xóa tài khoản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
    onSettled: () => {
      setDeletingAccount(null)
    },
  })

  const { mutate: resendActivationMail } = useMutation({
    mutationFn: (account: Account) =>
      sendActivationMail(account.id),
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Gửi lại mail kích hoạt thành công!'
      notification.success({ title: successMessage })
    },
  })

  const { mutate: resetPassword } = useMutation({
    mutationFn: (account: Account) =>
      sendResetPassword(account.id),
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Đặt lại mật khẩu mặc định thành công!'
      notification.success({ title: successMessage })
    },
  })

  const confirmDelete = (account: Account) => {
    setDeletingAccount(account)
  }

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        showUnitColumn,
        canView,
        canUpdate,
        canDelete,
        canResetPass,
        onViewDetail: (account) => setViewingAccount(account),
        onUpdate: (account) => {
          setFormMode('update')
          setEditingAccountId(account.id)
          setIsFormDrawerOpen(true)
        },
        onDelete: confirmDelete,
        onToggleStatus: (account) => setTogglingAccount(account),
        onResendActivationMail: resendActivationMail,
        onResetPassword: resetPassword,
      }),
    [
      pageIndex,
      pageSize,
      showUnitColumn,
      canView,
      canUpdate,
      canDelete,
      canResetPass,
      resendActivationMail,
      resetPassword,
    ],
  )

  const rows = useMemo(() => data?.items ?? [], [data?.items])

  const { handleExport } = useTableExport<Account>(
    'Danh sách tài khoản',
    columns,
    async (payload) => {
      try {
        setIsExporting(true)
        await exportUser(filterParams, payload, 'danh-sach-tai-khoan.xlsx')
      } finally {
        setIsExporting(false)
      }
    },
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH TÀI KHOẢN" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH TÀI KHOẢN"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
      >
        {canCreate && (
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 h-10"
            onClick={() => {
              setFormMode('create')
              setEditingAccountId(null)
              setIsFormDrawerOpen(true)
            }}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        )}
      </PageHeader>

      {showFilter && (
        <AccountFilterPanel
          values={filter}
          onChange={setFilter}
          showUnitColumn={showUnitColumn}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              fullName: filter.fullName.trim(),
              email: filter.email.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        tableOptions={{
          state: {
            pagination: { pageIndex, pageSize },
          },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />

      <AccountDetailDrawer
        accountId={viewingAccount?.id || null}
        onClose={() => setViewingAccount(null)}
        onUpdate={(account) => {
          setViewingAccount(null)
          setFormMode('update')
          setEditingAccountId(account.id)
          setIsFormDrawerOpen(true)
        }}
      />

      {isFormDrawerOpen && (
        <AccountFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          accountId={editingAccountId}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingAccountId(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!togglingAccount}
        title="Xác nhận"
        description={
          <>
            Bạn chắc chắn muốn{' '}
            {togglingAccount?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} tài
            khoản <strong>{togglingAccount?.fullName}</strong> này?
          </>
        }
        variant="info"
        isLoading={isToggling}
        onClose={() => setTogglingAccount(null)}
        onConfirm={() =>
          togglingAccount && toggleAccountStatus(togglingAccount)
        }
      />

      <ConfirmModal
        isOpen={!!deletingAccount}
        title="Xóa tài khoản"
        description={
          <>
            Bạn chắc chắn muốn xóa tài khoản{' '}
            <strong>{deletingAccount?.fullName}</strong> này?
          </>
        }
        variant="danger"
        isLoading={isRemoving}
        onClose={() => setDeletingAccount(null)}
        onConfirm={() => deletingAccount && removeAccount(deletingAccount)}
      />
    </section>
  )
}
